<script lang="ts">
export default {
  name: 'BinderReviewList',
};
</script>

<script setup lang="ts">
import { Loader } from '@lucide/vue';
import AppButton from '@/components/v2/AppButton.vue';
import type { BinderReviewItem, BinderReviewSource, BinderReviewStatus } from '@/types/BinderReview';

// A compact inline list of binder extractions, for panels that show a handful in passing.
// Filtering, searching and paging belong to the full list page; this stays small enough
// to sit inside a modal.
withDefaults(
  defineProps<{
    items: BinderReviewItem[];
    loading?: boolean;
  }>(),
  { loading: false },
);

const emit = defineEmits<{
  open: [item: BinderReviewItem];
}>();

const SOURCE_LABELS: Record<BinderReviewSource, string> = {
  internal: 'Policy',
  external: 'External',
};

const ACTION_LABELS: Record<BinderReviewStatus, string> = {
  ready: 'Review',
  reviewed: 'View',
  processing: 'Scanning',
  failed: 'Failed',
  discarded: 'Discarded',
};

// Only a result that exists can be opened; a binder still scanning has nothing to show.
const isOpenable = (item: BinderReviewItem) =>
  item.promptResultId != null && (item.status === 'ready' || item.status === 'reviewed');

// Each source's own stable identity: a submission keeps its id across the scan, while an
// extraction result is what identifies an internal binder's review.
const rowKey = (item: BinderReviewItem) =>
  `${item.source}-${(item.source === 'external' ? item.submissionId : item.promptResultId) ?? item.documentId}`;

const testId = (item: BinderReviewItem) => `open-binder-review-${rowKey(item)}`;
</script>

<template>
  <div data-testid="pending-binder-scans">
    <div
      v-if="loading"
      class="flex items-center gap-2">
      <Loader
        :size="16"
        class="animate-spin text-secondary" />
      <span class="text-sm">Loading…</span>
    </div>

    <p
      v-else-if="items.length === 0"
      class="text-sm text-base-content/60"
      data-testid="binder-review-empty">
      No binder scans waiting.
    </p>

    <ul
      v-else
      class="flex max-h-96 flex-col gap-2 overflow-y-auto">
      <li
        v-for="item in items"
        :key="rowKey(item)"
        class="flex items-center justify-between gap-3 rounded border border-base-300 p-2">
        <div class="flex min-w-0 flex-col">
          <div class="flex items-center gap-2">
            <span
              class="badge badge-sm shrink-0"
              :class="item.source === 'external' ? 'badge-warning' : 'badge-ghost'"
              :data-testid="`binder-review-source-${item.source}`">
              {{ SOURCE_LABELS[item.source] }}
            </span>
            <span class="truncate text-sm font-medium">{{ item.documentName }}</span>
          </div>
          <span
            v-if="item.policyNumber"
            class="truncate text-xs text-base-content/60">
            {{ item.policyNumber }}
          </span>
        </div>

        <!-- The control carries the state, so the row needs no subtext for it. Scanning
             matches the policies toolbar: warning outline with a spinner. -->
        <AppButton
          v-if="isOpenable(item)"
          outline
          success
          :data-testid="testId(item)"
          @click="emit('open', item)">
          {{ ACTION_LABELS[item.status] }}
        </AppButton>
        <AppButton
          v-else-if="item.status === 'processing'"
          outline
          warning
          loading
          disabled
          :data-testid="testId(item)">
          {{ ACTION_LABELS.processing }}
        </AppButton>
        <span
          v-else
          class="badge badge-sm shrink-0"
          :class="item.status === 'failed' ? 'badge-error' : 'badge-ghost'"
          :title="item.errorMessage"
          :data-testid="testId(item)">
          {{ ACTION_LABELS[item.status] }}
        </span>
      </li>
    </ul>
  </div>
</template>
